import { generateUUID } from '../utils/uuid';

// ── Room configuration defaults ──────────────────────────────────────────────
// Canonical default dimensions are 120×120×50. These match the backend room
// creation payload and API fallback values. Any file that needs a "blank room"
// should import from here instead of inlining magic numbers.

export const DEFAULT_ROOM_CONFIG = Object.freeze({
  scaleX: 120,
  scaleZ: 120,
  height: 50,
  materialType: 'basic',
  materialId: 0,
  materialOptions: {},
  shaderOptions: {},
  decorations: [],
  lightIntensity: 1.0,
  roomLiningVisible: true,
});

/** Return a mutable copy of DEFAULT_ROOM_CONFIG (safe to modify / pass to setState). */
export function createDefaultRoomConfig() {
  return {
    ...DEFAULT_ROOM_CONFIG,
    materialOptions: {},
    shaderOptions: {},
    decorations: [],
  };
}

// ── Model property defaults ──────────────────────────────────────────────────

export const DEFAULT_MODEL_PROPS = Object.freeze({
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
  roughness: 1,
  music: null,
  musicAssetKey: null,
  musicLoop: true,
  link: null,
  hoverText: null,
});

/**
 * Normalise a partial model object coming from the backend (or localStorage)
 * into the shape the frontend stores expect.
 *
 * @param {Object} model  – raw model data (may have missing / null fields)
 * @param {number} index  – position in the array, used for fallback name
 * @returns {Object}      – fully-populated model object
 */
export function applyModelDefaults(model, index = 0) {
  return {
    id: model.id || generateUUID(),
    name: model.name || `Model ${index + 1}`,
    url: model.url || '',
    position: Array.isArray(model.position) ? model.position : [...DEFAULT_MODEL_PROPS.position],
    rotation: Array.isArray(model.rotation) ? model.rotation : [...DEFAULT_MODEL_PROPS.rotation],
    scale: Array.isArray(model.scale) ? model.scale : [...DEFAULT_MODEL_PROPS.scale],
    hue: model.hue,
    roughness: model.roughness ?? DEFAULT_MODEL_PROPS.roughness,
    assetId: model.assetId,
    category: model.category || 'uncategorized',
    music: model.music || DEFAULT_MODEL_PROPS.music,
    musicAssetKey: model.musicAssetKey || DEFAULT_MODEL_PROPS.musicAssetKey,
    musicLoop: model.musicLoop ?? DEFAULT_MODEL_PROPS.musicLoop,
    link: model.link || DEFAULT_MODEL_PROPS.link,
    hoverText: model.hoverText || DEFAULT_MODEL_PROPS.hoverText,
    type: model.type || null,
    avatarConfig: model.avatarConfig || null,
    avatarId: model.avatarId || null,
  };
}

// ── Misc constants ───────────────────────────────────────────────────────────

/** Maximum undo/redo history entries. */
export const MAX_HISTORY_LENGTH = 50;

/** Maximum rooms a signed-in user can own. */
export const ROOM_LIMIT = 10;

// ── Branding ─────────────────────────────────────────────────────────────────

export const APP_NAME = 'Mogulplex';
export const APP_DOMAIN = 'mogulplex.com';
